import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn } from '../../lib/utils';
import {
  ArrowLeft, User, Calendar, Phone, Mail, MapPin, Shield, Pill, AlertTriangle,
  TestTube, Clock, FileText, MessageSquareText, CreditCard, Stethoscope,
  DollarSign, Send, Eye, ScanLine, Plus, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import CrossAppDialog from '../../components/shared/CrossAppDialog';
import { useAppMode } from '../../contexts/AppModeContext';

const PATIENTS_DATA = {
  P001: {
    id: 'P001', firstName: 'John', lastName: 'Smith', dob: '1965-03-15', age: 60, gender: 'Male',
    mrn: 'MRN-001', phone: '(555) 123-4567', email: 'john.smith@email.com',
    address: '123 Main St, Springfield, IL 62701',
    insurance: { name: 'Blue Cross Blue Shield', memberId: 'BCB-123456789', group: 'GRP-9876', copay: '$30', verified: true },
    appointments: [
      { date: '2026-03-17', time: '9:00 AM', type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled' },
      { date: '2026-02-03', time: '10:00 AM', type: 'Follow-up', provider: 'Dr. Chen', status: 'completed' },
      { date: '2026-01-06', time: '9:30 AM', type: 'Sick Visit', provider: 'Dr. Wilson', status: 'completed' },
    ],
    timeline: [
      { date: '2026-03-16', type: 'sms', desc: 'Appointment reminder sent for 3/17', agent: 'Follow-Up Agent' },
      { date: '2026-02-03', type: 'visit', desc: 'Follow-up visit completed with Dr. Chen', agent: null },
      { date: '2026-02-01', type: 'call', desc: 'Patient called to reschedule, handled by Front Desk Agent', agent: 'Front Desk Agent' },
      { date: '2026-01-28', type: 'intake', desc: 'Insurance card uploaded and verified via Echo Lens', agent: 'Intake Agent' },
      { date: '2026-01-06', type: 'visit', desc: 'Sick visit — cough and fever, completed', agent: null },
      { date: '2025-12-15', type: 'email', desc: 'Annual wellness reminder email sent', agent: 'Follow-Up Agent' },
    ],
    billing: { copayCollected: 90, copayPending: 30, outstanding: 120, lastPayment: '2026-02-03' },
    clinicalSummary: { problems: ['Type 2 Diabetes (E11.9)', 'Hypertension (I10)', 'Hyperlipidemia (E78.5)'], medications: ['Metformin 1000mg', 'Lisinopril 20mg', 'Atorvastatin 40mg'], allergies: ['Penicillin', 'Sulfa Drugs'], lastProvider: 'Dr. Chen' },
  },
};

// Duplicate data for other patient IDs
['P002','P003','P004','P005','P006','1','2','3','4','5','6','7','8'].forEach((id, i) => {
  if (!PATIENTS_DATA[id]) {
    const names = [['Maria','Garcia'],['Robert','Johnson'],['Emily','Davis'],['Sarah','Williams'],['Michael','Brown'],['John','Smith'],['Maria','Garcia'],['Robert','Johnson'],['Emily','Davis'],['James','Taylor'],['Sarah','Williams'],['David','Wilson'],['Lisa','Brown']];
    const n = names[i] || ['Patient', id];
    PATIENTS_DATA[id] = { ...PATIENTS_DATA.P001, id, firstName: n[0], lastName: n[1], mrn: `MRN-${String(i+2).padStart(3,'0')}` };
  }
});

const TIMELINE_ICONS = { sms: MessageSquareText, visit: Calendar, call: Phone, intake: ScanLine, email: Mail };
const TIMELINE_COLORS = { sms: 'bg-blue-100 text-blue-600', visit: 'bg-emerald-100 text-emerald-600', call: 'bg-amber-100 text-amber-600', intake: 'bg-violet-100 text-violet-600', email: 'bg-slate-100 text-slate-600' };

const EchoPatientChart = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDemo } = useAppMode();
  const [activeTab, setActiveTab] = useState('overview');
  const [crossApp, setCrossApp] = useState({ open: false, target: 'lumina', context: '' });

  const patient = PATIENTS_DATA[id] || PATIENTS_DATA.P001;
  // Gate the synthetic per-patient activity timeline in real mode so no
  // fake SMS/call/visit events leak into an authenticated tenant. Demo mode
  // keeps the showcase timeline for onboarding/tours.
  const timeline = isDemo ? (patient.timeline || []) : [];

  const promptClinical = (action) => setCrossApp({ open: true, target: 'lumina', context: `"${action}" requires Lumina — the clinical documentation platform.` });
  const promptBilling = (action) => setCrossApp({ open: true, target: 'revq', context: `"${action}" requires revQ — the billing & claims platform.` });

  return (
    <div className="space-y-6" data-testid="echo-patient-chart">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/echo/patients')} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center"><User className="h-6 w-6 text-slate-500" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">{patient.firstName} {patient.lastName}</h1>
          <p className="text-sm text-muted-foreground">{patient.mrn} &middot; {patient.gender}, {patient.age}y &middot; DOB: {patient.dob}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => toast.success('Calling patient...')} data-testid="call-btn"><Phone className="h-4 w-4" /> Call</Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => toast.success('SMS sent')} data-testid="sms-btn"><MessageSquareText className="h-4 w-4" /> SMS</Button>
          <Button size="sm" className="bg-black hover:bg-gray-800 gap-2" onClick={() => navigate('/echo/lens')} data-testid="schedule-btn"><Calendar className="h-4 w-4" /> Schedule</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="appointments">Appointments</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="clinical">Clinical</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><User className="h-4 w-4 text-slate-500" />Demographics</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{patient.phone}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{patient.email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="text-right max-w-[200px]">{patient.address}</span></div>
            </CardContent></Card>

            <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-blue-500" />Insurance</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><Badge variant="outline">{patient.insurance.name}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Member ID</span><span>{patient.insurance.memberId}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Group</span><span>{patient.insurance.group}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Copay</span><span>{patient.insurance.copay}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span>{patient.insurance.verified ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Verified</Badge> : <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending</Badge>}</div>
            </CardContent></Card>

            <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Calendar className="h-4 w-4 text-emerald-500" />Next Appointment</CardTitle></CardHeader><CardContent>
              {patient.appointments[0] && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-medium">{patient.appointments[0].date}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Time</span><span>{patient.appointments[0].time}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Type</span><Badge variant="outline">{patient.appointments[0].type}</Badge></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Provider</span><span>{patient.appointments[0].provider}</span></div>
                  <Button variant="outline" size="sm" className="w-full mt-2 gap-2" onClick={() => toast.success('Reminder sent')}><Send className="h-3 w-3" /> Send Reminder</Button>
                </div>
              )}
            </CardContent></Card>
          </div>

          {/* Recent Timeline */}
          <Card className="border-slate-200 mt-4"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent Activity</CardTitle></CardHeader><CardContent>
            {timeline.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="recent-activity-empty">
                <Clock className="h-6 w-6 text-slate-300 mb-2" />
                <p className="text-xs text-slate-500">No recent activity yet</p>
                <p className="text-[10px] text-slate-400 mt-0.5">SMS, calls, visits and intake events will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {timeline.slice(0, 4).map((t, i) => {
                  const Icon = TIMELINE_ICONS[t.type] || Clock;
                  const color = TIMELINE_COLORS[t.type] || 'bg-slate-100 text-slate-600';
                  return (
                    <div key={i} className="flex items-start gap-3" data-testid={`timeline-${i}`}>
                      <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0", color.split(' ')[1])}><Icon className={cn("h-3.5 w-3.5", color.split(' ')[0])} /></div>
                      <div className="flex-1"><p className="text-sm text-slate-700">{t.desc}</p><p className="text-[10px] text-muted-foreground">{t.date}{t.agent && ` · ${t.agent}`}</p></div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Appointments */}
        <TabsContent value="appointments">
          <Card className="border-slate-200">
            <CardHeader className="pb-3"><div className="flex items-center justify-between"><CardTitle className="text-sm font-semibold">Appointment History</CardTitle><Button size="sm" className="bg-black hover:bg-gray-800 gap-2" onClick={() => navigate('/echo/lens')}><Plus className="h-3 w-3" /> New Appointment</Button></div></CardHeader>
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Provider</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {patient.appointments.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium">{a.date}</TableCell>
                    <TableCell className="text-sm">{a.time}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{a.type}</Badge></TableCell>
                    <TableCell className="text-sm">{a.provider}</TableCell>
                    <TableCell><Badge className={cn("text-[10px]", a.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : a.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600')}>{a.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline">
          <Card className="border-slate-200"><CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Patient Timeline</CardTitle></CardHeader><CardContent>
            {timeline.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="patient-timeline-empty">
                <Clock className="h-7 w-7 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No patient timeline yet</p>
                <p className="text-xs text-slate-400 mt-0.5">Activity for this patient will appear here as your team works in the app.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {timeline.map((t, i) => {
                  const Icon = TIMELINE_ICONS[t.type] || Clock;
                  const color = TIMELINE_COLORS[t.type] || 'bg-slate-100 text-slate-600';
                  return (
                    <div key={i} className="flex items-start gap-3 relative" data-testid={`full-timeline-${i}`}>
                      {i < timeline.length - 1 && <div className="absolute left-[13px] top-8 bottom-0 w-px bg-slate-200" />}
                      <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0 z-10", color.split(' ')[1])}><Icon className={cn("h-3.5 w-3.5", color.split(' ')[0])} /></div>
                      <div className="flex-1 pb-4">
                        <p className="text-sm text-slate-700">{t.desc}</p>
                        <div className="flex items-center gap-2 mt-1"><p className="text-[10px] text-muted-foreground">{t.date}</p>{t.agent && <Badge variant="outline" className="text-[9px] px-1.5">{t.agent}</Badge>}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* Clinical (read-only, prompts to Lumina) */}
        <TabsContent value="clinical">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" />Active Problems</CardTitle></CardHeader><CardContent>
              <div className="space-y-2">{patient.clinicalSummary.problems.map((p, i) => <Badge key={i} variant="outline" className="mr-1 mb-1 text-xs">{p}</Badge>)}</div>
              <p className="text-[10px] text-muted-foreground mt-3 italic">Read-only view from Lumina</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Pill className="h-4 w-4 text-blue-500" />Medications</CardTitle></CardHeader><CardContent>
              <div className="space-y-1.5">{patient.clinicalSummary.medications.map((m, i) => <p key={i} className="text-sm text-slate-700">{m}</p>)}</div>
              <p className="text-[10px] text-muted-foreground mt-3 italic">Read-only view from Lumina</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TestTube className="h-4 w-4 text-amber-500" />Allergies</CardTitle></CardHeader><CardContent>
              <div className="space-y-1.5">{patient.clinicalSummary.allergies.map((a, i) => <Badge key={i} className="bg-red-100 text-red-700 text-xs mr-1">{a}</Badge>)}</div>
            </CardContent></Card>
          </div>
          <Card className="border-slate-200 mt-4 bg-slate-50"><CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><Stethoscope className="h-5 w-5 text-slate-400" /><div><p className="text-sm font-medium text-slate-700">Need to document clinically?</p><p className="text-xs text-muted-foreground">SOAP notes, ambient listening, and clinical documentation require Lumina.</p></div></div>
            <Button onClick={() => promptClinical('Clinical Documentation')} className="bg-black hover:bg-gray-800 gap-2" data-testid="go-to-lumina-btn"><Stethoscope className="h-4 w-4" /> Open in Lumina</Button>
          </CardContent></Card>
        </TabsContent>

        {/* Billing (front-office view, prompts to revQ for full billing) */}
        <TabsContent value="billing">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Copay Collected', value: `$${patient.billing.copayCollected}`, color: 'text-emerald-600 bg-emerald-50', icon: CheckCircle2 },
              { label: 'Copay Pending', value: `$${patient.billing.copayPending}`, color: 'text-amber-600 bg-amber-50', icon: Clock },
              { label: 'Outstanding', value: `$${patient.billing.outstanding}`, color: 'text-red-600 bg-red-50', icon: CreditCard },
              { label: 'Last Payment', value: patient.billing.lastPayment, color: 'text-blue-600 bg-blue-50', icon: Calendar },
            ].map((s, i) => (
              <Card key={i} className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
                <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", s.color.split(' ')[1])}><s.icon className={cn("h-4 w-4", s.color.split(' ')[0])} /></div>
                <div><p className="text-lg font-bold">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
              </CardContent></Card>
            ))}
          </div>
          <Card className="border-slate-200 bg-slate-50"><CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><DollarSign className="h-5 w-5 text-slate-400" /><div><p className="text-sm font-medium text-slate-700">Need full billing & claims?</p><p className="text-xs text-muted-foreground">Claims submission, denial management, and AR require revQ.</p></div></div>
            <Button onClick={() => promptBilling('Claims & Billing')} className="bg-black hover:bg-gray-800 gap-2" data-testid="go-to-revq-btn"><DollarSign className="h-4 w-4" /> Open in revQ</Button>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <CrossAppDialog open={crossApp.open} onOpenChange={(v) => setCrossApp({ ...crossApp, open: v })} targetApp={crossApp.target} context={crossApp.context} />
    </div>
  );
};

export default EchoPatientChart;
